import { useFormContext } from "react-hook-form";
import Button from "components/Button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

export default function BidItems({ items }) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const [itemStates, setItemStates] = useState(
    items.map((item) => ({
      id: item.id,
      isNoQuote: false,
      isBidding: item.is_bidded || false,
    }))
  );

  // Watch form values for items
  const formItems = watch("items") || [];

  const handleNoQuote = (itemId, index) => {
    setItemStates((prev) =>
      prev.map((state) =>
        state.id === itemId
          ? { ...state, isNoQuote: true, isBidding: false }
          : state
      )
    );

    // Update form values
    setValue(`items.${index}.is_no_quote`, true);
    setValue(`items.${index}.quantity`, null);
    setValue(`items.${index}.unit_price`, null);
    setValue(`items.${index}.quotation_item_id`, itemId);
  };

  const handleAddBid = (itemId, index) => {
    setItemStates((prev) =>
      prev.map((state) =>
        state.id === itemId
          ? { ...state, isNoQuote: false, isBidding: true }
          : state
      )
    );

    // Reset no quote status and set item ID
    setValue(`items.${index}.is_no_quote`, false);
    setValue(`items.${index}.quotation_item_id`, itemId);
  };

  const handleCancelBid = (itemId, index) => {
    setItemStates((prev) =>
      prev.map((state) =>
        state.id === itemId
          ? { ...state, isNoQuote: false, isBidding: false }
          : state
      )
    );

    // Reset form values for this item
    setValue(`items.${index}`, {
      quotation_item_id: itemId,
      is_no_quote: false,
      quantity: null,
      unit_price: null,
    });
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const itemState = itemStates[index];
        const itemErrors = errors?.items?.[index];

        // Initialize form field if not already done
        if (!formItems[index]) {
          setValue(`items.${index}`, {
            quotation_item_id: item.id,
            is_no_quote: false,
            quantity: item.is_bidded ? Number(item.bidded_quantity) : 0,
            unit_price: item.is_bidded ? Number(item.bidded_unit_price) : 0,
          });
        }

        return (
          <div key={item.id} className="bg-white overflow-hidden">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 text-blue-600 border-blue-300 border rounded-lg flex items-center justify-center font-semibold">
                {index + 1}
              </div>
              <div className="pb-4 flex-1">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-blue-600">
                      Item No : {index + 1}
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 max-w-2xl">
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            item.description ||
                            "Tender Item header information will goes here",
                        }}
                      ></span>
                    </h3>
                  </div>
                  <div className="space-x-2 mr-0.5 flex items-center">
                    {!itemState.isBidding && !itemState.isNoQuote && (
                      <Button
                        variant="outline"
                        color="slate"
                        className="h-7 text-xs font-bold"
                        onClick={() => handleNoQuote(item.id, index)}
                      >
                        No Quote
                      </Button>
                    )}
                    {itemState.isNoQuote && (
                      <Button
                        variant="outline"
                        color="slate"
                        className="h-7 text-xs font-bold flex items-center gap-1"
                        onClick={() => handleCancelBid(item.id, index)}
                      >
                        <XMarkIcon className="h-4 w-4" />
                        No Quote Marked
                      </Button>
                    )}
                    {!itemState.isNoQuote &&
                      (itemState.isBidding ? (
                        <Button
                          variant="outline"
                          color="slate"
                          className={`h-7 text-xs font-bold  ${
                            item.is_bidded ? "hidden" : ""
                          }`}
                          onClick={() => handleCancelBid(item.id, index)}
                        >
                          Cancel Bid
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          color="primary"
                          className="h-7 text-xs font-bold"
                          onClick={() => handleAddBid(item.id, index)}
                        >
                          Add Bid
                        </Button>
                      ))}
                  </div>
                </div>

                <div className="col-span-4 space-y-2 divide-y">
                  <div className="grid grid-cols-12">
                    <div className="col-span-3 text-sm">
                      Unit of Measurement
                    </div>
                    <div className="col-span-9 font-semibold text-sm">
                      {item.uom || "-"}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 pt-2">
                    <div className="col-span-3 text-sm">Required Quantity</div>
                    <div className="col-span-9 font-semibold text-sm">
                      {item.estimate_quantity || "-"}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 pt-2">
                    <div className="col-span-3 text-sm">Remark</div>
                    <div className="col-span-9 font-semibold text-sm">
                      {item.remark || "No"}
                    </div>
                  </div>
                </div>

                {itemState.isBidding && (
                  <div className="mt-6 grid grid-cols-4 p-4 gap-2 rounded-md border border-blue-300 bg-blue-100">
                    <div className="col-span-1">
                      <h3 className="text-sm text-gray-900 font-semibold">
                        Based Bid *
                      </h3>
                      <p className="text-xs text-gray-500">
                        Submitting Currency will be default to the tender
                      </p>
                    </div>
                    <div className="col-span-2">
                      <div>
                        <label
                          htmlFor={`items.${index}.quantity`}
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Quantity<span className="text-blue-600">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            {...register(`items.${index}.quantity`)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                          />
                          {itemErrors?.quantity && (
                            <p className="mt-1 text-xs text-red-600">
                              {itemErrors.quantity.message}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-4">
                        <label
                          htmlFor={`items.${index}.unit_price`}
                          className="block text-sm font-medium leading-6 text-gray-900"
                        >
                          Unit Price<span className="text-blue-600">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            step="0.01"
                            {...register(`items.${index}.unit_price`)}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-600 sm:text-sm sm:leading-6"
                          />
                          {itemErrors?.unit_price && (
                            <p className="mt-1 text-xs text-red-600">
                              {itemErrors.unit_price.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
